import React, { useState, useEffect } from 'react';
import '../css/App.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheckCircle,
  faEdit,
  faCopy,
  faPlus,
  faTrash,
  faSave,
  faTimes,
  faCheck,
  faInfoCircle,
  faList,
  faSlidersH,
  faEye,
  faCode,
  faStar,
  faSearch
} from '@fortawesome/free-solid-svg-icons';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import axios from './axiosSetup';
import API_BASE from './apiBase';
import Swal from 'sweetalert2';
import dpdpConsentConfig from './ConsentBannerconfig';
import DpdpConsentInterface from './ConsentBannerInterface';
import { getStrings, LANGUAGE_NAMES } from './data/emailLang';

// ─── Icons (inline, no external deps) ────────────────────────────────────────

const Icon = {
  Shield: ({ size = 20, color = '#2563eb' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path d="M12 2l8 3v6c0 5-3.4 9-8 11-4.6-2-8-6-8-11V5l8-3z" stroke={color} strokeWidth="1.8" />
      <path d="M9 12l2 2 4-4" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  Doc: ({ size = 18, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path d="M6 2h9l5 5v15H6V2z" stroke={color} strokeWidth="1.8" />
      <path d="M9 12h6M9 16h6M9 8h3" stroke={color} strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  ),
  Bell: ({ size = 18, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path d="M18 8a6 6 0 10-12 0c0 7-3 9-3 9h18s-3-2-3-9" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M13.7 21a2 2 0 01-3.4 0" stroke={color} strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  ),
  User: ({ size = 16, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="8" r="4" stroke={color} strokeWidth="1.8" />
      <path d="M4 21c0-4 4-6 8-6s8 2 8 6" stroke={color} strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  ),
  Mail: ({ size = 16, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <rect x="3" y="5" width="18" height="14" rx="2" stroke={color} strokeWidth="1.8" />
      <path d="M3 7l9 6 9-6" stroke={color} strokeWidth="1.8" />
    </svg>
  ),
  Phone: ({ size = 16, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path d="M5 4h4l2 5-2.5 1.5a11 11 0 005 5L15 13l5 2v4a2 2 0 01-2 2C9.6 21 3 14.4 3 6a2 2 0 012-2z" stroke={color} strokeWidth="1.8" strokeLinejoin="round" />
    </svg>
  ),
  Grid: ({ size = 16, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <rect x="3" y="3" width="7" height="7" rx="1.4" stroke={color} strokeWidth="1.8" />
      <rect x="14" y="3" width="7" height="7" rx="1.4" stroke={color} strokeWidth="1.8" />
      <rect x="3" y="14" width="7" height="7" rx="1.4" stroke={color} strokeWidth="1.8" />
      <rect x="14" y="14" width="7" height="7" rx="1.4" stroke={color} strokeWidth="1.8" />
    </svg>
  ),
  Lock: ({ size = 13, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <rect x="4" y="10" width="16" height="10" rx="2" stroke={color} strokeWidth="1.8" />
      <path d="M8 10V7a4 4 0 018 0v3" stroke={color} strokeWidth="1.8" />
    </svg>
  ),
  Chevron: ({ size = 14, open }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }}>
      <path d="M6 9l6 6 6-6" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
};

const DATA_ICONS = { Name: Icon.User, Email: Icon.Mail, Mobile: Icon.Phone, Documents: Icon.Doc, 'Usage Data': Icon.Grid };
const PURPOSE_ICONS = { doc: Icon.Doc, shield: Icon.Shield, bell: Icon.Bell };

const getConsentEndpoint = (apiBase) => {
  const base = (apiBase || API_BASE || '').toString().trim().replace(/\/+$/, '');
  if (!base) return '/api/manager/dpdp-consent';
  return base.endsWith('/api')
    ? `${base}/manager/dpdp-consent`
    : `${base}/api/manager/dpdp-consent`;
};

const getNativeSdkCode = (platform, config, endpoint, bannerId) => {
  const rawTenantId = config.tenantId || dpdpConsentConfig.getTenantIdFromStorage() || 'YOUR_TENANT_ID';
  const tenantId = rawTenantId === 'YOUR_TENANT_ID' ? 'YOUR_TENANT_ID' : dpdpConsentConfig.encryptTenantId(rawTenantId);
  const appName = config.appName || 'OneDPDP';
  const title = config.title || 'Data Privacy Consent';
  const subtitle = config.subtitle || '';
  const primaryColor = config.primaryColor || '#2563eb';
  const bId = bannerId || 'banner_default';

  if (platform === 'ios') {
    return `import Foundation

enum ConsentDataSDK {
  static let endpoint = URL(string: ${JSON.stringify(endpoint)})!
  static let tenantId = ${JSON.stringify(String(tenantId))}
  static let bannerId = ${JSON.stringify(String(bId))}
  static let appName = ${JSON.stringify(appName)}
  static let bannerTitle = ${JSON.stringify(title)}
  static let bannerSubtitle = ${JSON.stringify(subtitle)}
  static let primaryColorHex = ${JSON.stringify(primaryColor)}

  static func submit(status: String, preferences: [String: Bool] = [:]) async throws {
    var request = URLRequest(url: endpoint)
    request.httpMethod = "POST"
    request.setValue("application/json", forHTTPHeaderField: "Content-Type")
    request.httpBody = try JSONSerialization.data(withJSONObject: [
      "tenant_id": tenantId,
      "banner_id": bannerId,
      "consent_status": status,
      "session_id": UUID().uuidString,
      "preferences": preferences,
      "app_name": appName
    ])

    let (_, response) = try await URLSession.shared.data(for: request)
    guard let http = response as? HTTPURLResponse, (200..<300).contains(http.statusCode) else {
      throw URLError(.badServerResponse)
    }
  }
}

// Call after the user makes a choice:
Task {
  try await ConsentDataSDK.submit(status: "accepted", preferences: ["analytics": true])
}`;
  }

  return `import org.json.JSONObject
import java.io.IOException
import java.net.HttpURLConnection
import java.net.URL
import java.util.UUID

object ConsentDataSDK {
  private const val endpoint = ${JSON.stringify(endpoint)}
  const val tenantId = ${JSON.stringify(String(tenantId))}
  const val bannerId = ${JSON.stringify(String(bId))}
  const val appName = ${JSON.stringify(appName)}
  const val bannerTitle = ${JSON.stringify(title)}
  const val bannerSubtitle = ${JSON.stringify(subtitle)}
  const val primaryColorHex = ${JSON.stringify(primaryColor)}

  fun submit(status: String, preferences: Map<String, Boolean> = emptyMap()) {
    val connection = URL(endpoint).openConnection() as HttpURLConnection
    try {
      connection.requestMethod = "POST"
      connection.setRequestProperty("Content-Type", "application/json")
      connection.doOutput = true
      val payload = JSONObject()
        .put("tenant_id", tenantId)
        .put("banner_id", bannerId)
        .put("consent_status", status)
        .put("session_id", UUID.randomUUID().toString())
        .put("preferences", JSONObject(preferences))
        .put("app_name", appName)
      connection.outputStream.use { it.write(payload.toString().toByteArray(Charsets.UTF_8)) }
      if (connection.responseCode !in 200..299) throw IOException("Consent request failed")
    } finally {
      connection.disconnect()
    }
  }
}

// Call on a background thread after the user makes a choice:
Thread { ConsentDataSDK.submit("accepted", mapOf("analytics" to true)) }.start()`;
};

// ─── Live Preview — combines the strongest elements of both reference designs ──

export function DpdpConsentPreview({ config, onAccept, onDecline, onPreferences, tenantName }) {
  const [lang, setLang] = useState('en');
  const t = getStrings(lang);
  const [openRow, setOpenRow] = useState(null);
  const [checked, setChecked] = useState({ notice: false, consent: false });
  const [toggles, setToggles] = useState(() => {
    const t = {};
    (config?.categories || []).forEach(c => { t[c.id] = !!c.defaultOn; });
    return t;
  });

  useEffect(() => {
    const t = {};
    (config?.categories || []).forEach(c => { t[c.id] = !!c.defaultOn; });
    setToggles(t);
  }, [config?.categories]);

  const cfg = config || {};
  const primary = cfg.primaryColor || '#2563eb';
  const styles = dpdpConsentConfig.getWidgetStyles(cfg);

  const sizeMap = { small: '360px', medium: '480px', large: '620px', fullscreen: '100%' };
  const maxWidth = sizeMap[cfg.size] || '480px';
  const isFullscreen = cfg.size === 'fullscreen';

  const canAccept = !cfg.requireCheckbox || (checked.notice && checked.consent);

  const rootVars = {
    '--dpdp-primary': primary,
    '--dpdp-bg': cfg.backgroundColor || '#ffffff',
    '--dpdp-radius': styles.container.borderRadius,
    '--dpdp-shadow': styles.container.boxShadow,
    '--dpdp-btn-radius': styles.button.borderRadius,
    '--dpdp-maxw': maxWidth,
  };

  return (
    <div className={`dpdp-widget-card${isFullscreen ? ' is-fullscreen' : ''}`} style={rootVars}>
      {/* Header */}
      <div className="dpdp-widget-header">
        <div className="dpdp-widget-brand">
          <div className="dpdp-widget-badge">
            <Icon.Shield size={20} color="#fff" />
          </div>
          <div>
            <div className="dpdp-widget-brand-name">{tenantName || cfg.appName || 'OneDPDP'}</div>
            <div className="dpdp-widget-brand-sub">{cfg.subBrand || 'Enterprise Privacy'}</div>
          </div>
        </div>
      </div>

      {/* Title / subtitle */}
      <div className="dpdp-widget-title">{cfg.title || 'Data Privacy Consent'}</div>
      <div className="dpdp-widget-subtitle">{cfg.subtitle || 'We respect your privacy. Please review how your personal data will be used.'}</div>

      {/* Purpose of Processing */}
      <div className="dpdp-purpose-box">
        <div className="dpdp-purpose-box-title">Purpose of Processing</div>
        {(cfg.purposes || []).map((p, i) => {
          const IconComp = PURPOSE_ICONS[p.icon] || Icon.Doc;
          const open = openRow === 'p' + i;
          return (
            <div key={p.id || i}
              onClick={() => setOpenRow(open ? null : 'p' + i)}
              className={`dpdp-purpose-row${i === 0 ? ' no-border' : ''}`}
              style={{ cursor: 'pointer' }}
            >
              <div className="dpdp-purpose-icon">
                <IconComp size={16} color={primary} />
              </div>
              <div className="dpdp-purpose-text">
                <div className="dpdp-purpose-label">{p.label}</div>
                {open && <div className="dpdp-purpose-desc">{p.description}</div>}
              </div>
              <span className="dpdp-required-badge">Required</span>
              <span style={{ marginTop: 3 }}><Icon.Chevron open={open} /></span>
            </div>
          );
        })}
        {(cfg.categories || []).map((c, i) => (
          <div key={c.id || i} className="dpdp-purpose-row">
            <div className="dpdp-category-icon">
              <Icon.Grid size={16} />
            </div>
            <div className="dpdp-purpose-text">
              <div className="dpdp-purpose-label">{c.label}</div>
              <div className="dpdp-purpose-desc">{c.description}</div>
            </div>
            <button
              type="button"
              aria-pressed={!!toggles[c.id]}
              onClick={(e) => {
                e.stopPropagation();
                setToggles(prev => ({ ...prev, [c.id]: !prev[c.id] }));
              }}
              className={`dpdp-toggle${toggles[c.id] ? ' is-on' : ''}`}>
              <div className="dpdp-toggle-knob" />
            </button>
          </div>
        ))}
      </div>

      {/* Data We Collect */}
      {cfg.showDataCollected && (
        <div className="dpdp-data-box">
          <div className="dpdp-data-title">Data We Collect</div>
          <div className="dpdp-data-chips">
            {(cfg.dataCollected || []).map((label, i) => {
              const DIcon = DATA_ICONS[label] || Icon.Doc;
              return (
                <div key={i} className="dpdp-data-chip">
                  <span className="dpdp-data-chip-icon"><DIcon size={15} color={primary} /></span>
                  <span className="dpdp-data-chip-label">{label}</span>
                </div>
              );
            })}
          </div>
          <div className="dpdp-data-lock-row">
            <Icon.Lock size={12} /> Your data is secure and will never be sold.
          </div>
        </div>
      )}

      {/* Control note */}
      <div className="dpdp-control-note">
        <span style={{ marginTop: 1 }}><Icon.Shield size={18} color="#059669" /></span>
        <span className="dpdp-control-note-text">
          <b>You're in Control.</b> You can withdraw or update your consent anytime from settings.
        </span>
      </div>

      {/* Checkboxes */}
      <div className="dpdp-checkbox-group">
        <label className="dpdp-checkbox-label">
          <input type="checkbox" checked={checked.notice} onChange={() => setChecked(s => ({ ...s, notice: !s.notice }))} />
          <span>I have read and understood the{' '}
            <a href={cfg.privacyUrl || '/privacy-policy'} target="_blank" rel="noreferrer">Privacy Notice</a>.
          </span>
        </label>
        <label className="dpdp-checkbox-label">
          <input type="checkbox" checked={checked.consent} onChange={() => setChecked(s => ({ ...s, consent: !s.consent }))} />
          <span>I consent to the collection and processing of my personal data for the purposes mentioned above.</span>
        </label>
      </div>

      {/* Actions */}
      <div className="dpdp-action-row">
        <button className="dpdp-btn dpdp-btn-outline" onClick={() => onDecline && onDecline('declined')}>{t.decline}</button>
        <button className="dpdp-btn dpdp-btn-select" onClick={() => onPreferences && onPreferences()}>
          Allow Selection
        </button>
        <button className="dpdp-btn dpdp-btn-primary" disabled={!canAccept} onClick={() => canAccept && onAccept && onAccept('accepted', toggles)}>
          {t.accept}
        </button>
      </div>

      {/* Footer */}
      <div className="dpdp-widget-footer">
        <Icon.Shield size={13} color="#94a3b8" /> {tenantName || cfg.appName || 'OneDPDP'} {cfg.complianceLabel || 'is DPDP Act 2023 Compliant'}
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

function DpdpConsentPage({ tenantId, onSave, onBack }) {
  const [banners, setBanners] = useState([]);
  const [activeBanner, setActiveBanner] = useState(null);
  const [config, setConfig] = useState(dpdpConsentConfig.getConfig());
  const [tenantName, setTenantName] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [codePlatform, setCodePlatform] = useState('web');
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('theme');

  // Multi-Banner Management State
  const [showNewModal, setShowNewModal] = useState(false);
  const [newBannerName, setNewBannerName] = useState('');
  const [newBannerDesc, setNewBannerDesc] = useState('');
  const [newBannerTemplate, setNewBannerTemplate] = useState('duplicate');
  const [creatingBanner, setCreatingBanner] = useState(false);
  const [viewMode, setViewMode] = useState('customizer'); // 'customizer' | 'all-banners'
  const [bannerSearch, setBannerSearch] = useState('');
  const [previewModalBanner, setPreviewModalBanner] = useState(null);
  const [bannerLimit, setBannerLimit] = useState(null);

  // Purpose / Category inline add state
  const [newPurposeLabel, setNewPurposeLabel] = useState('');
  const [newPurposeDesc, setNewPurposeDesc] = useState('');
  const [newPurposeIcon, setNewPurposeIcon] = useState('doc');

  const [newCatLabel, setNewCatLabel] = useState('');
  const [newCatDesc, setNewCatDesc] = useState('');
  const [newCatDefault, setNewCatDefault] = useState(false);

  const [newDataTag, setNewDataTag] = useState('');

  const getResolvedTenantId = () => {
    let tid = tenantId || config.tenantId;
    if (!tid) {
      try { tid = localStorage.getItem('tenant_id'); } catch (e) { }
    }
    if (!tid) {
      try {
        const u = localStorage.getItem('user');
        if (u) {
          const parsed = JSON.parse(u);
          tid = parsed.tenant_id || parsed.tenantId;
        }
      } catch (e) { }
    }
    return tid || dpdpConsentConfig.getTenantIdFromStorage();
  };

  const getApiBase = () => {
    let runtimeBase = '';
    try { runtimeBase = (window && window.__API_BASE__) ? window.__API_BASE__ : API_BASE || ''; }
    catch (e) { runtimeBase = API_BASE || ''; }
    return (runtimeBase || '').toString().replace(/\/$/, '');
  };

  const normalizeConfig = (rawCfg) => {
    const base = dpdpConsentConfig.loadConfig();
    if (!rawCfg) return base;
    try {
      const parsed = typeof rawCfg === 'string' ? JSON.parse(rawCfg) : rawCfg;
      return {
        ...base,
        ...parsed,
        purposes: (parsed && Array.isArray(parsed.purposes) && parsed.purposes.length > 0)
          ? parsed.purposes
          : base.purposes,
        categories: (parsed && Array.isArray(parsed.categories) && parsed.categories.length > 0)
          ? parsed.categories
          : base.categories,
        dataCollected: (parsed && Array.isArray(parsed.dataCollected))
          ? parsed.dataCollected
          : base.dataCollected,
      };
    } catch (e) {
      return base;
    }
  };

  // 1. Load Banners for this tenant from Database
  const loadTenantBanners = async () => {
    const tid = getResolvedTenantId();
    if (!tid) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const bannerRes = await dpdpConsentConfig.fetchBannerData(tid, getApiBase());
      const list = bannerRes.data;
      if (bannerRes.limit !== null && bannerRes.limit !== undefined) {
        setBannerLimit(Number(bannerRes.limit));
      }
      if (Array.isArray(list) && list.length > 0) {
        setBanners(list);
        // Find default banner or first banner
        const def = list.find(b => b.is_default) || list[0];
        setActiveBanner(def);
        if (def && def.config) {
          const bannerCfg = normalizeConfig(def.config);
          setConfig(bannerCfg);
          dpdpConsentConfig.saveConfig(bannerCfg);
        }
      } else {
        // Fallback default banner
        const fallbackBanner = {
          id: 'local_default',
          banner_id: 'banner_default',
          name: 'Main Privacy Banner',
          description: 'Primary DPDP consent banner',
          is_default: true,
          config: dpdpConsentConfig.getConfig()
        };
        setBanners([fallbackBanner]);
        setActiveBanner(fallbackBanner);
      }
    } catch (err) {
      console.error('Failed to load DPDP banners:', err);
      setError('Could not load banners from server. Using local copy.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTenantBanners();
  }, [tenantId]);

  // Load Tenant Name & Limit
  useEffect(() => {
    const tid = getResolvedTenantId();
    if (!tid) return;
    const url = (getApiBase() ? getApiBase() : '') + '/admin/tenants';
    let cancelled = false;
    axios.get(url)
      .then(res => {
        if (cancelled) return;
        const list = Array.isArray(res.data) ? res.data : (res.data && res.data.data) ? res.data.data : [];
        const found = list.find(t => Number(t.id) === Number(tid));
        setTenantName(found && found.name ? found.name : 'Tenant ' + tid);
        if (found) {
          const lim = found.consent_banner_limit ?? found.limit ?? found.limits;
          if (lim !== undefined && lim !== null && lim !== '') {
            setBannerLimit(Number(lim));
          }
        }
      })
      .catch(() => { if (!cancelled) setTenantName('Tenant ' + tid); });
    return () => { cancelled = true; };
  }, [tenantId]);

  // Switch Banner
  const handleSelectBanner = (bannerIdVal) => {
    const found = banners.find(b => String(b.banner_id) === String(bannerIdVal) || String(b.id) === String(bannerIdVal));
    if (found) {
      setActiveBanner(found);
      const bannerCfg = normalizeConfig(found.config);
      setConfig(bannerCfg);
      dpdpConsentConfig.saveConfig(bannerCfg);
    }
  };

  // Update Config locally
  const updateConfigField = (field, value) => {
    setConfig(prev => {
      const next = { ...prev, [field]: value };
      dpdpConsentConfig.saveConfig(next);
      return next;
    });
  };

  // Add / Remove Purpose
  const handleAddPurpose = () => {
    if (!newPurposeLabel.trim()) return;
    const pId = 'p_' + Date.now().toString(36);
    const updatedPurposes = [
      ...(config.purposes || []),
      { id: pId, label: newPurposeLabel.trim(), description: newPurposeDesc.trim(), icon: newPurposeIcon }
    ];
    updateConfigField('purposes', updatedPurposes);
    setNewPurposeLabel('');
    setNewPurposeDesc('');
  };

  const handleRemovePurpose = (pId) => {
    const updatedPurposes = (config.purposes || []).filter(p => p.id !== pId);
    updateConfigField('purposes', updatedPurposes);
  };

  // Add / Remove Preference Category
  const handleAddCategory = () => {
    if (!newCatLabel.trim()) return;
    const cId = 'cat_' + Date.now().toString(36);
    const updatedCats = [
      ...(config.categories || []),
      { id: cId, label: newCatLabel.trim(), description: newCatDesc.trim(), defaultOn: !!newCatDefault }
    ];
    updateConfigField('categories', updatedCats);
    setNewCatLabel('');
    setNewCatDesc('');
    setNewCatDefault(false);
  };

  const handleRemoveCategory = (cId) => {
    const updatedCats = (config.categories || []).filter(c => c.id !== cId);
    updateConfigField('categories', updatedCats);
  };

  const handleToggleCategoryDefault = (cId) => {
    const updatedCats = (config.categories || []).map(c => {
      if (c.id === cId) return { ...c, defaultOn: !c.defaultOn };
      return c;
    });
    updateConfigField('categories', updatedCats);
  };

  // Add / Remove Data Collected Tag
  const handleAddDataTag = () => {
    if (!newDataTag.trim()) return;
    const updated = [...(config.dataCollected || []), newDataTag.trim()];
    updateConfigField('dataCollected', updated);
    setNewDataTag('');
  };

  const handleRemoveDataTag = (idx) => {
    const updated = (config.dataCollected || []).filter((_, i) => i !== idx);
    updateConfigField('dataCollected', updated);
  };

  // Save to Database
  const handleSaveToDatabase = async () => {
    setSaving(true);
    setError('');
    try {
      const tid = getResolvedTenantId();
      if (!activeBanner) {
        throw new Error('No banner selected to save');
      }

      const payload = {
        name: activeBanner.name || 'Consent Banner',
        description: activeBanner.description || '',
        is_default: !!activeBanner.is_default,
        is_active: true,
        config: config,
        tenant_id: tid
      };

      const res = await dpdpConsentConfig.saveBannerToDb(activeBanner.id || activeBanner.banner_id, payload, tid, getApiBase());
      if (res && res.success && res.data) {
        // Update local banner list
        setBanners(prev => prev.map(b => (b.id === activeBanner.id ? res.data : b)));
        setActiveBanner(res.data);
      }

      setSuccessMessage('Banner configuration saved to database successfully!');
      setSuccess(true);
      if (onSave) onSave(config);
      setTimeout(() => { setSuccess(false); setSuccessMessage(''); }, 3000);
    } catch (e) {
      console.error('Save to DB failed:', e);
      setError('Failed to save to database. Check server connection.');
    } finally {
      setSaving(false);
    }
  };

  // Create New Banner in Database
  const handleCreateNewBanner = async () => {
    if (!newBannerName.trim()) {
      Swal.fire({ icon: 'info', title: 'Banner Name Required', text: 'Please enter a banner name' });
      return;
    }
    const tid = getResolvedTenantId();
    if (!tid) {
      Swal.fire({ icon: 'error', title: 'Tenant Error', text: 'Tenant ID could not be identified. Please ensure you are logged in as a tenant.' });
      return;
    }
    if (bannerLimit && banners.length >= bannerLimit) {
      Swal.fire({
        icon: 'warning',
        title: 'Banner Limit Reached',
        text: `You have reached the maximum allowed limit of ${bannerLimit} Consent Management Banner(s) for your organization. Cannot create more banners.`,
        confirmButtonColor: '#2563eb'
      });
      return;
    }
    setCreatingBanner(true);
    try {
      const baseConfig = newBannerTemplate === 'duplicate' ? { ...config } : dpdpConsentConfig.loadConfig();
      baseConfig.appName = tenantName || config.appName;

      const payload = {
        tenant_id: tid,
        name: newBannerName.trim(),
        description: newBannerDesc.trim(),
        is_default: banners.length === 0,
        is_active: true,
        config: baseConfig
      };

      const res = await dpdpConsentConfig.createBannerInDb(payload, getApiBase());
      if (res && res.data) {
        const created = res.data;
        setBanners(prev => [...prev, created]);
        setActiveBanner(created);
        const parsed = normalizeConfig(created.config);
        setConfig(parsed);
        dpdpConsentConfig.saveConfig(parsed);
        setShowNewModal(false);
        setNewBannerName('');
        setNewBannerDesc('');
        setSuccessMessage(`New banner "${created.name}" created successfully!`);
        setSuccess(true);
        setTimeout(() => { setSuccess(false); setSuccessMessage(''); }, 3000);
      } else {
        throw new Error('Banner was not returned by server');
      }
    } catch (e) {
      console.error('Create banner failed:', e);
      const errMsg = e.response?.data?.detail || e.message || 'Unknown error';
      Swal.fire({
        icon: 'error',
        title: 'Cannot Create Banner',
        text: errMsg,
        confirmButtonColor: '#2563eb'
      });
    } finally {
      setCreatingBanner(false);
    }
  };

  // Set as Default (active banner or any selected banner from list)
  const handleSetDefault = async (targetBanner = activeBanner) => {
    const bannerToSet = targetBanner || activeBanner;
    if (!bannerToSet) return;
    try {
      const tid = getResolvedTenantId();
      await dpdpConsentConfig.setDefaultBannerInDb(bannerToSet.id || bannerToSet.banner_id, tid, getApiBase());
      setBanners(prev => prev.map(b => ({
        ...b,
        is_default: (b.id === bannerToSet.id || b.banner_id === bannerToSet.banner_id)
      })));
      if (activeBanner?.id === bannerToSet.id || activeBanner?.banner_id === bannerToSet.banner_id) {
        setActiveBanner(prev => ({ ...prev, is_default: true }));
      } else {
        setActiveBanner(prev => prev ? ({ ...prev, is_default: false }) : prev);
      }
      setSuccessMessage(`"${bannerToSet.name}" is now the default banner!`);
      setSuccess(true);
      setTimeout(() => { setSuccess(false); setSuccessMessage(''); }, 2500);
    } catch (e) {
      alert('Failed to set default banner');
    }
  };

  // Delete Banner (active banner or any selected banner from list)
  const handleDeleteBanner = async (targetBanner = activeBanner) => {
    const bannerToDelete = targetBanner || activeBanner;
    if (!bannerToDelete) return;
    if (banners.length <= 1) {
      alert('Cannot delete the only banner. At least one banner must remain.');
      return;
    }
    if (!window.confirm(`Are you sure you want to delete "${bannerToDelete.name}"?`)) return;

    try {
      const tid = getResolvedTenantId();
      await dpdpConsentConfig.deleteBannerFromDb(bannerToDelete.id || bannerToDelete.banner_id, tid, getApiBase());
      const remaining = banners.filter(b => b.id !== bannerToDelete.id && b.banner_id !== bannerToDelete.banner_id);
      setBanners(remaining);
      if (activeBanner?.id === bannerToDelete.id || activeBanner?.banner_id === bannerToDelete.banner_id) {
        const nextActive = remaining[0];
        setActiveBanner(nextActive);
        const parsed = normalizeConfig(nextActive.config);
        setConfig(parsed);
        dpdpConsentConfig.saveConfig(parsed);
      }
      if (previewModalBanner?.id === bannerToDelete.id || previewModalBanner?.banner_id === bannerToDelete.banner_id) {
        setPreviewModalBanner(null);
      }
      setSuccessMessage('Banner deleted successfully!');
      setSuccess(true);
      setTimeout(() => { setSuccess(false); setSuccessMessage(''); }, 2500);
    } catch (e) {
      alert('Failed to delete banner');
    }
  };

  // Copy embed script for any banner
  const copyEmbedCodeForBanner = (b) => {
    if (!b) return;
    const bannerCfg = normalizeConfig(b.config);
    const code = dpdpConsentConfig.getEmbedCode(b.banner_id, bannerCfg);
    navigator.clipboard.writeText(code).then(() => {
      setSuccessMessage(`Embed script for "${b.name}" copied to clipboard!`);
      setSuccess(true);
      setTimeout(() => { setSuccess(false); setSuccessMessage(''); }, 2500);
    });
  };

  // Select banner and switch to Customizer
  const handleEditBannerFromList = (b) => {
    if (!b) return;
    handleSelectBanner(b.banner_id);
    setViewMode('customizer');
  };

  // Send consent simulation
  const sendConsent = async (status, prefs) => {
    setError('');
    try {
      const payload = {
        tenant_id: getResolvedTenantId() || 0,
        banner_id: activeBanner?.banner_id || 'banner_default',
        consent_status: status,
        session_id: dpdpConsentConfig.getSessionId(),
        preferences: prefs || {},
      };
      const endpoint = (getApiBase() ? `${getApiBase()}` : '') + '/api/manager/dpdp-consent';
      await axios.post(endpoint, payload);
      setSuccessMessage(status === 'accepted' ? 'Accepted successfully (Test event recorded)' : 'Declined successfully');
      setSuccess(true);
      setTimeout(() => { setSuccess(false); setSuccessMessage(''); }, 2000);
    } catch (e) {
      console.warn('Simulation consent post error:', e);
    }
  };

  const integrationCode = codePlatform === 'web'
    ? dpdpConsentConfig.getEmbedCode(activeBanner?.banner_id, config)
    : getNativeSdkCode(codePlatform, config, getConsentEndpoint(config.apiBase), activeBanner?.banner_id);

  const copyIntegrationCode = () => {
    navigator.clipboard.writeText(integrationCode).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="dpdp-consent-page" style={{ padding: '0 0.5rem 2rem' }}>
      {/* Top Header */}
      <div className="page-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', marginTop: '-0.25rem', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h2 className="page-title" style={{ margin: 0 }}>Consent Management Banner</h2>
          <span style={{ fontSize: '0.85rem', color: '#64748b' }}>
            {/* Tenant: <b>{tenantName || 'Tenant ' + (getResolvedTenantId() || '')}</b> • Total Banners: <b>{banners.length}{bannerLimit ? ` / ${bannerLimit}` : ''}</b> */}
            {bannerLimit && banners.length >= bannerLimit ? (
              <span style={{ marginLeft: 8, color: '#dc2626', fontWeight: 600 }}> (Limit Reached)</span>
            ) : null}
          </span>
        </div>

        {/* View Switcher: All Banners vs Customizer */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={() => {
              if (bannerLimit && banners.length >= bannerLimit) {
                Swal.fire({
                  icon: 'warning',
                  title: 'Banner Limit Reached',
                  text: `You have reached the maximum allowed limit of ${bannerLimit} Consent Management Banner(s) for your organization. You cannot create more banners.`,
                  confirmButtonColor: '#2563eb'
                });
                return;
              }
              setShowNewModal(true);
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              background: bannerLimit && banners.length >= bannerLimit ? '#94a3b8' : '#059669',
              color: '#ffffff',
              border: 'none',
              padding: '7px 15px',
              borderRadius: '8px',
              fontWeight: 700,
              fontSize: '0.86rem',
              cursor: bannerLimit && banners.length >= bannerLimit ? 'not-allowed' : 'pointer',
              boxShadow: bannerLimit && banners.length >= bannerLimit ? 'none' : '0 2px 5px rgba(5,150,105,0.25)',
              transition: 'all 0.15s ease'
            }}
            title={bannerLimit && banners.length >= bannerLimit ? 'Banner limit reached' : 'Create a new banner for this tenant'}
          >
            <FontAwesomeIcon icon={faPlus} />
            <span>Create New Banner</span>
          </button>

          <div style={{
            display: 'inline-flex',
            background: '#f1f5f9',
            padding: '3px',
            borderRadius: '9px',
            border: '1px solid #cbd5e1'
          }}>
            <button
              type="button"
              onClick={() => setViewMode('all-banners')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '7px',
                padding: '7px 14px',
                borderRadius: '7px',
                border: 'none',
                fontSize: '0.86rem',
                fontWeight: 700,
                cursor: 'pointer',
                background: viewMode === 'all-banners' ? '#2563eb' : 'transparent',
                color: viewMode === 'all-banners' ? '#ffffff' : '#475569',
                boxShadow: viewMode === 'all-banners' ? '0 1px 3px rgba(37,99,235,0.3)' : 'none',
                transition: 'all 0.15s'
              }}
            >
              <FontAwesomeIcon icon={faList} />
              <span>All Banners ({banners.length})</span>
            </button>
            <button
              type="button"
              onClick={() => setViewMode('customizer')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '7px',
                padding: '7px 14px',
                borderRadius: '7px',
                border: 'none',
                fontSize: '0.86rem',
                fontWeight: 700,
                cursor: 'pointer',
                background: viewMode === 'customizer' ? '#2563eb' : 'transparent',
                color: viewMode === 'customizer' ? '#ffffff' : '#475569',
                boxShadow: viewMode === 'customizer' ? '0 1px 3px rgba(37,99,235,0.3)' : 'none',
                transition: 'all 0.15s'
              }}
            >
              <FontAwesomeIcon icon={faSlidersH} />
              <span>Banner Customizer</span>
            </button>
          </div>

          <button
            className="back-btn"
            onClick={() => {
              if (typeof onBack === 'function') onBack();
              else window.history.back();
            }}
          >
            Back
          </button>
        </div>
      </div>

      {/* Notifications */}
      {success && (
        <div style={{ background: '#dcfce7', border: '1px solid #86efac', color: '#15803d', padding: '10px 16px', borderRadius: 8, marginBottom: '1rem', fontWeight: 600 }}>
          ✓ {successMessage}
        </div>
      )}
      {error && (
        <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', color: '#b91c1c', padding: '10px 16px', borderRadius: 8, marginBottom: '1rem', fontWeight: 600 }}>
          ⚠ {error}
        </div>
      )}

      {/* Main Content: All Banners Table View OR Customizer */}
      {viewMode === 'all-banners' ? (
        /* ═════════════════════════════════════════════════════════════════════
           ALL BANNERS MANAGEMENT TABLE & OVERVIEW (See all banners together)
           ═════════════════════════════════════════════════════════════════════ */
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
          {/* Table Container Card */}
          <div style={{ background: '#ffffff', border: '1.5px solid #e2e8f0', borderRadius: 12, padding: '18px 20px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
            {/* Search and Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
              <div style={{ position: 'relative', minWidth: '280px', flex: 1, maxWidth: '420px' }}>
                <FontAwesomeIcon icon={faSearch} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: '0.85rem' }} />
                <input
                  type="text"
                  placeholder="Search banners by name, banner ID, app..."
                  value={bannerSearch}
                  onChange={e => setBannerSearch(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 12px 8px 34px',
                    borderRadius: 8,
                    border: '1.5px solid #cbd5e1',
                    fontSize: '0.88rem',
                    outline: 'none'
                  }}
                />
              </div>

              <div style={{ fontSize: '0.85rem', color: '#64748b' }}>
                Showing <b>{banners.filter(b => {
                  if (!bannerSearch.trim()) return true;
                  const q = bannerSearch.toLowerCase();
                  return (b.name || '').toLowerCase().includes(q) || (b.banner_id || '').toLowerCase().includes(q) || (b.description || '').toLowerCase().includes(q);
                }).length}</b> of <b>{banners.length}</b> consent banners
              </div>
            </div>

            {/* Banners Table */}
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.88rem' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                    <th style={{ padding: '12px 14px', color: '#475569', fontWeight: 700, fontSize: '0.82rem' }}>BANNER DETAILS</th>
                    <th style={{ padding: '12px 14px', color: '#475569', fontWeight: 700, fontSize: '0.82rem' }}>TARGET APP / SERVICE</th>
                    <th style={{ padding: '12px 14px', color: '#475569', fontWeight: 700, fontSize: '0.82rem' }}>PURPOSES & PREFERENCES</th>
                    <th style={{ padding: '12px 14px', color: '#475569', fontWeight: 700, fontSize: '0.82rem' }}>STATUS</th>
                    <th style={{ padding: '12px 14px', color: '#475569', fontWeight: 700, fontSize: '0.82rem', textAlign: 'right' }}>ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {banners
                    .filter(b => {
                      if (!bannerSearch.trim()) return true;
                      const q = bannerSearch.toLowerCase();
                      return (b.name || '').toLowerCase().includes(q) || (b.banner_id || '').toLowerCase().includes(q) || (b.description || '').toLowerCase().includes(q);
                    })
                    .map((b, idx) => {
                      const bCfg = normalizeConfig(b.config);
                      const isCurrent = activeBanner?.id === b.id || activeBanner?.banner_id === b.banner_id;
                      const purposeCount = (bCfg.purposes && Array.isArray(bCfg.purposes)) ? bCfg.purposes.length : 0;
                      const catCount = (bCfg.categories && Array.isArray(bCfg.categories)) ? bCfg.categories.length : 0;
                      const dataCount = (bCfg.dataCollected && Array.isArray(bCfg.dataCollected)) ? bCfg.dataCollected.length : 0;

                      return (
                        <tr
                          key={b.banner_id || b.id || idx}
                          style={{
                            borderBottom: '1px solid #f1f5f9',
                            background: isCurrent ? '#f0f9ff' : 'transparent',
                            transition: 'background 0.15s'
                          }}
                        >
                          {/* Banner Details */}
                          <td style={{ padding: '14px', verticalAlign: 'top' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span style={{ fontWeight: 700, color: '#0f172a', fontSize: '0.94rem' }}>
                                {b.name}
                              </span>
                              {b.is_default && (
                                <span style={{ background: '#fef3c7', color: '#b45309', border: '1px solid #fde68a', padding: '2px 8px', borderRadius: 5, fontSize: '0.72rem', fontWeight: 700 }}>
                                  ★ Default
                                </span>
                              )}
                              {isCurrent && (
                                <span style={{ background: '#dbeafe', color: '#1e40af', padding: '2px 8px', borderRadius: 5, fontSize: '0.72rem', fontWeight: 700 }}>
                                  Selected
                                </span>
                              )}
                            </div>
                            {b.description && (
                              <div style={{ fontSize: '0.78rem', color: '#64748b', marginTop: 3 }}>
                                {b.description}
                              </div>
                            )}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: 5 }}>
                              <code style={{ fontSize: '0.75rem', background: '#f1f5f9', color: '#475569', padding: '2px 6px', borderRadius: 4, border: '1px solid #e2e8f0' }}>
                                {b.banner_id}
                              </code>
                              <button
                                type="button"
                                onClick={() => {
                                  navigator.clipboard.writeText(b.banner_id);
                                  setSuccessMessage(`Banner ID "${b.banner_id}" copied!`);
                                  setSuccess(true);
                                  setTimeout(() => { setSuccess(false); setSuccessMessage(''); }, 2000);
                                }}
                                title="Copy Banner ID"
                                style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', padding: 2, fontSize: '0.75rem' }}
                              >
                                <FontAwesomeIcon icon={faCopy} />
                              </button>
                            </div>
                          </td>

                          {/* Target App */}
                          <td style={{ padding: '14px', verticalAlign: 'top' }}>
                            <div style={{ fontWeight: 600, color: '#334155', fontSize: '0.88rem' }}>
                              {bCfg.appName || tenantName || 'OneDPDP Service'}
                            </div>
                            {/* <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: 2 }}>
                              Tenant ID: {b.tenant_id}
                            </div> */}
                          </td>

                          {/* Purposes & Preferences */}
                          <td style={{ padding: '14px', verticalAlign: 'top' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                              <span style={{
                                width: 14,
                                height: 14,
                                borderRadius: '50%',
                                background: bCfg.primaryColor || '#2563eb',
                                border: '1px solid #cbd5e1',
                                display: 'inline-block'
                              }} title={`Theme Color: ${bCfg.primaryColor || '#2563eb'}`} />
                              <span style={{ fontSize: '0.8rem', color: '#475569', textTransform: 'capitalize' }}>
                                {bCfg.theme || 'Light'} • {bCfg.size || 'Medium'} • {bCfg.containerShape || 'Rounded'}
                              </span>
                            </div>
                            <div style={{ fontSize: '0.76rem', color: '#059669', fontWeight: 600, marginTop: 4 }}>
                              ✓ {purposeCount} purposes • {catCount} preferences • {dataCount} data tags
                            </div>
                          </td>

                          {/* Status */}
                          <td style={{ padding: '14px', verticalAlign: 'top' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                              <span style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '5px',
                                fontSize: '0.76rem',
                                color: '#16a34a',
                                fontWeight: 700
                              }}>
                                <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#16a34a' }}></span>
                                Active
                              </span>
                              {b.is_default ? (
                                <span style={{ fontSize: '0.74rem', color: '#b45309', fontWeight: 600 }}>
                                  Default Consent Banner
                                </span>
                              ) : (
                                <span style={{ fontSize: '0.74rem', color: '#94a3b8' }}>
                                  Secondary Banner
                                </span>
                              )}
                            </div>
                          </td>

                          {/* Actions */}
                          <td style={{ padding: '14px', verticalAlign: 'top', textAlign: 'right' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '6px', flexWrap: 'wrap' }}>
                              {/* Customize Button */}
                              <button
                                type="button"
                                onClick={() => handleEditBannerFromList(b)}
                                style={{
                                  background: '#2563eb',
                                  color: '#fff',
                                  border: 'none',
                                  padding: '6px 12px',
                                  borderRadius: 6,
                                  fontSize: '0.78rem',
                                  fontWeight: 700,
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '5px'
                                }}
                                title="Open this banner in Customizer"
                              >
                                <FontAwesomeIcon icon={faEdit} /> Customize
                              </button>

                              {/* Preview Button */}
                              <button
                                type="button"
                                onClick={() => setPreviewModalBanner(b)}
                                style={{
                                  background: '#f0fdf4',
                                  border: '1px solid #bbf7d0',
                                  color: '#15803d',
                                  padding: '6px 10px',
                                  borderRadius: 6,
                                  fontSize: '0.78rem',
                                  fontWeight: 600,
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '5px'
                                }}
                                title="Quick Preview this banner"
                              >
                                <FontAwesomeIcon icon={faEye} /> Preview
                              </button>

                              {/* Copy Embed Code */}
                              <button
                                type="button"
                                onClick={() => copyEmbedCodeForBanner(b)}
                                style={{
                                  background: '#f8fafc',
                                  border: '1px solid #cbd5e1',
                                  color: '#334155',
                                  padding: '6px 10px',
                                  borderRadius: 6,
                                  fontSize: '0.78rem',
                                  fontWeight: 600,
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '5px'
                                }}
                                title="Copy HTML embed script for this banner"
                              >
                                <FontAwesomeIcon icon={faCode} /> Embed Code
                              </button>

                              {/* Set as Default (if not default) */}
                              {!b.is_default && (
                                <button
                                  type="button"
                                  onClick={() => handleSetDefault(b)}
                                  style={{
                                    background: '#fefce8',
                                    border: '1px solid #fde047',
                                    color: '#854d0e',
                                    padding: '6px 10px',
                                    borderRadius: 6,
                                    fontSize: '0.76rem',
                                    fontWeight: 700,
                                    cursor: 'pointer'
                                  }}
                                  title="Make this the default banner for this tenant"
                                >
                                  ★ Make Default
                                </button>
                              )}

                              {/* Delete Banner */}
                              {banners.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => handleDeleteBanner(b)}
                                  style={{
                                    background: '#fee2e2',
                                    border: '1px solid #fecaca',
                                    color: '#b91c1c',
                                    padding: '6px 10px',
                                    borderRadius: 6,
                                    fontSize: '0.76rem',
                                    cursor: 'pointer'
                                  }}
                                  title="Delete this banner"
                                >
                                  <FontAwesomeIcon icon={faTrash} />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>

              {banners.filter(b => {
                if (!bannerSearch.trim()) return true;
                const q = bannerSearch.toLowerCase();
                return (b.name || '').toLowerCase().includes(q) || (b.banner_id || '').toLowerCase().includes(q) || (b.description || '').toLowerCase().includes(q);
              }).length === 0 && (
                  <div style={{ textAlign: 'center', padding: '40px 20px', color: '#64748b' }}>
                    <p style={{ margin: 0, fontSize: '0.95rem' }}>No consent banners match "<b>{bannerSearch}</b>"</p>
                    <button
                      type="button"
                      onClick={() => setBannerSearch('')}
                      style={{ marginTop: 10, background: '#f1f5f9', border: '1px solid #cbd5e1', padding: '6px 14px', borderRadius: 6, cursor: 'pointer', fontSize: '0.82rem' }}
                    >
                      Clear Search
                    </button>
                  </div>
                )}
            </div>
          </div>
        </div>
      ) : (
        /* ═════════════════════════════════════════════════════════════════════
           CUSTOMIZER VIEW (Interactive 3-Column Studio)
           ═════════════════════════════════════════════════════════════════════ */
        <div className="dpdp-panel-row" style={{ display: 'grid', gridTemplateColumns: 'minmax(320px, 380px) minmax(360px, 1fr) minmax(300px, 340px)', gap: '1.25rem' }}>

          {/* 1. Customization Controls Panel */}
          <div className="dpdp-panel-controls" style={{ background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 12, padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>

            {/* Sub Navigation Tabs */}
            <div style={{ display: 'flex', gap: '4px', borderBottom: '1.5px solid #e2e8f0', paddingBottom: '8px', overflowX: 'auto' }}>
              {[
                { id: 'theme', label: 'Theme & Style' },
                { id: 'text', label: 'Content' },
                { id: 'purposes', label: 'Purposes' },
                { id: 'categories', label: 'Preferences' },
                { id: 'data', label: 'Data Tags' },
              ].map(tab => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  style={{
                    background: activeTab === tab.id ? '#2563eb' : 'transparent',
                    color: activeTab === tab.id ? '#fff' : '#64748b',
                    border: 'none',
                    padding: '6px 10px',
                    borderRadius: 6,
                    fontWeight: 600,
                    fontSize: '0.78rem',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* TAB 1: Theme & Style */}
            {activeTab === 'theme' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <div className="dpdp-design-row">
                  <label>Banner Name (Admin):</label>
                  <input
                    type="text"
                    value={activeBanner?.name || ''}
                    onChange={e => {
                      const val = e.target.value;
                      setActiveBanner(prev => ({ ...prev, name: val }));
                    }}
                  />
                </div>

                <div className="dpdp-design-row">
                  <label>App / Brand Name:</label>
                  <input type="text" value={config.appName || ''} onChange={e => updateConfigField('appName', e.target.value)} />
                </div>

                <div className="dpdp-design-row">
                  <label>Sub Brand:</label>
                  <input type="text" value={config.subBrand || ''} onChange={e => updateConfigField('subBrand', e.target.value)} />
                </div>

                <div className="dpdp-design-row">
                  <label>Primary Color:</label>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', width: '100%' }}>
                    <input type="color" value={config.primaryColor || '#2563eb'} onChange={e => updateConfigField('primaryColor', e.target.value)} style={{ width: 40, height: 32, cursor: 'pointer', border: 'none', background: 'transparent' }} />
                    <input type="text" value={config.primaryColor || '#2563eb'} onChange={e => updateConfigField('primaryColor', e.target.value)} style={{ flex: 1 }} />
                  </div>
                </div>

                <div className="dpdp-design-row">
                  <label>Background Color:</label>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', width: '100%' }}>
                    <input type="color" value={config.backgroundColor || '#ffffff'} onChange={e => updateConfigField('backgroundColor', e.target.value)} style={{ width: 40, height: 32, cursor: 'pointer', border: 'none', background: 'transparent' }} />
                    <input type="text" value={config.backgroundColor || '#ffffff'} onChange={e => updateConfigField('backgroundColor', e.target.value)} style={{ flex: 1 }} />
                  </div>
                </div>

                <div className="dpdp-design-row">
                  <label>Button Style:</label>
                  <select value={config.buttonStyle || 'rounded'} onChange={e => updateConfigField('buttonStyle', e.target.value)}>
                    <option value="rounded">Rounded</option>
                    <option value="square">Square</option>
                    <option value="pill">Pill</option>
                  </select>
                </div>

                <div className="dpdp-design-row">
                  <label>Placement:</label>
                  <select value={config.placement || 'center'} onChange={e => updateConfigField('placement', e.target.value)}>
                    <option value="center">Centered Modal</option>
                    <option value="full">Full-Screen Gate</option>
                  </select>
                </div>

                <div className="dpdp-design-row">
                  <label>Container Shade:</label>
                  <select value={config.shade || 'light'} onChange={e => updateConfigField('shade', e.target.value)}>
                    <option value="light">Light Shadow</option>
                    <option value="medium">Medium Shadow</option>
                    <option value="dark">Dark Shadow</option>
                  </select>
                </div>

                <div className="dpdp-design-row">
                  <label>Container Shape:</label>
                  <select value={config.containerShape || 'rounded'} onChange={e => updateConfigField('containerShape', e.target.value)}>
                    <option value="rounded">Rounded Corners</option>
                    <option value="square">Square</option>
                    <option value="rectangle">Rectangle</option>
                  </select>
                </div>

                <div className="dpdp-design-row">
                  <label>Size:</label>
                  <select value={config.size || 'medium'} onChange={e => updateConfigField('size', e.target.value)}>
                    <option value="small">Small</option>
                    <option value="medium">Medium</option>
                    <option value="large">Large</option>
                    <option value="fullscreen">Full Screen</option>
                  </select>
                </div>
              </div>
            )}

            {/* TAB 2: Text & Content */}
            {activeTab === 'text' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <div className="dpdp-design-row">
                  <label>Banner Title:</label>
                  <input type="text" value={config.title || ''} onChange={e => updateConfigField('title', e.target.value)} />
                </div>

                <div className="dpdp-design-row">
                  <label>Banner Subtitle:</label>
                  <textarea rows={2} value={config.subtitle || ''} onChange={e => updateConfigField('subtitle', e.target.value)} style={{ width: '100%', borderRadius: 6, border: '1.5px solid #cbd5e1', padding: '6px' }} />
                </div>

                <div className="dpdp-design-row">
                  <label>Compliance Statement:</label>
                  <input type="text" value={config.complianceLabel || ''} onChange={e => updateConfigField('complianceLabel', e.target.value)} />
                </div>

                <div className="dpdp-design-row">
                  <label>Privacy Policy URL:</label>
                  <input type="text" value={config.privacyUrl || ''} onChange={e => updateConfigField('privacyUrl', e.target.value)} />
                </div>

                <div className="dpdp-design-row">
                  <label>User Rights URL:</label>
                  <input type="text" value={config.rightsUrl || ''} onChange={e => updateConfigField('rightsUrl', e.target.value)} />
                </div>

                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 0' }}>
                  <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#334155' }}>Require Both Declarations</span>
                  <input type="checkbox" checked={config.requireCheckbox !== false} onChange={e => updateConfigField('requireCheckbox', e.target.checked)} />
                </div>

                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 0' }}>
                  <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#334155' }}>Show Multi-Step Progress</span>
                  <input type="checkbox" checked={!!config.showStepProgress} onChange={e => updateConfigField('showStepProgress', e.target.checked)} />
                </div>

                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 0' }}>
                  <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#334155' }}>Show Data Collected Box</span>
                  <input type="checkbox" checked={config.showDataCollected !== false} onChange={e => updateConfigField('showDataCollected', e.target.checked)} />
                </div>
              </div>
            )}

            {/* TAB 3: Required Purposes */}
            {activeTab === 'purposes' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <div style={{ fontSize: '0.8rem', color: '#64748b' }}>
                  Mandatory data processing purposes under DPDP Act 2023. These cannot be disabled by users.
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '280px', overflowY: 'auto' }}>
                  {(config.purposes || []).map((p, i) => (
                    <div key={p.id || i} style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 10px', background: '#f8fafc', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 700, fontSize: '0.85rem', color: '#0f172a' }}>{p.label}</div>
                        <div style={{ fontSize: '0.74rem', color: '#64748b', marginTop: 2 }}>{p.description}</div>
                        <div style={{ fontSize: '0.7rem', color: '#059669', fontWeight: 600, marginTop: 4 }}>Required • Icon: {p.icon}</div>
                      </div>
                      <button
                        onClick={() => handleRemovePurpose(p.id)}
                        style={{ background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer', fontWeight: 700, fontSize: '1rem', padding: '0 4px' }}
                        title="Remove Purpose"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>

                {/* Add New Purpose Form */}
                <div style={{ borderTop: '1.5px solid #e2e8f0', paddingTop: '10px', marginTop: '6px' }}>
                  <div style={{ fontWeight: 700, fontSize: '0.85rem', marginBottom: '6px', color: '#1e293b' }}>+ Add Required Purpose</div>
                  <input
                    type="text"
                    placeholder="Purpose Label (e.g. Identity Verification)"
                    value={newPurposeLabel}
                    onChange={e => setNewPurposeLabel(e.target.value)}
                    style={{ width: '100%', padding: '6px 8px', fontSize: '0.82rem', borderRadius: 6, border: '1px solid #cbd5e1', marginBottom: 6 }}
                  />
                  <input
                    type="text"
                    placeholder="Purpose Description"
                    value={newPurposeDesc}
                    onChange={e => setNewPurposeDesc(e.target.value)}
                    style={{ width: '100%', padding: '6px 8px', fontSize: '0.82rem', borderRadius: 6, border: '1px solid #cbd5e1', marginBottom: 6 }}
                  />
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <select
                      value={newPurposeIcon}
                      onChange={e => setNewPurposeIcon(e.target.value)}
                      style={{ flex: 1, padding: '6px', fontSize: '0.82rem', borderRadius: 6, border: '1px solid #cbd5e1' }}
                    >
                      <option value="doc">Icon: Document</option>
                      <option value="shield">Icon: Shield</option>
                      <option value="bell">Icon: Bell</option>
                      <option value="lock">Icon: Lock</option>
                    </select>
                    <button
                      onClick={handleAddPurpose}
                      style={{ background: '#2563eb', color: '#fff', border: 'none', padding: '6px 14px', borderRadius: 6, fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer' }}
                    >
                      Add
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* TAB 4: Optional Categories / Preferences */}
            {activeTab === 'categories' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <div style={{ fontSize: '0.8rem', color: '#64748b' }}>
                  Optional consent categories that users can toggle ON or OFF according to their preferences.
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '280px', overflowY: 'auto' }}>
                  {(config.categories || []).map((c, i) => (
                    <div key={c.id || i} style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 10px', background: '#f8fafc', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 700, fontSize: '0.85rem', color: '#0f172a' }}>{c.label}</div>
                        <div style={{ fontSize: '0.74rem', color: '#64748b', marginTop: 2 }}>{c.description}</div>
                        <div style={{ fontSize: '0.72rem', color: c.defaultOn ? '#10b981' : '#64748b', fontWeight: 600, marginTop: 4 }}>
                          Default: {c.defaultOn ? 'ON' : 'OFF'}
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <button
                          type="button"
                          onClick={() => handleToggleCategoryDefault(c.id)}
                          style={{
                            background: c.defaultOn ? '#10b981' : '#e2e8f0',
                            color: c.defaultOn ? '#fff' : '#475569',
                            border: 'none',
                            padding: '3px 8px',
                            borderRadius: 4,
                            fontSize: '0.72rem',
                            fontWeight: 700,
                            cursor: 'pointer'
                          }}
                        >
                          {c.defaultOn ? 'Default ON' : 'Default OFF'}
                        </button>
                        <button
                          onClick={() => handleRemoveCategory(c.id)}
                          style={{ background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer', fontWeight: 700, fontSize: '1.1rem', padding: '0 4px' }}
                          title="Remove Category"
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Add New Category Form */}
                <div style={{ borderTop: '1.5px solid #e2e8f0', paddingTop: '10px', marginTop: '6px' }}>
                  <div style={{ fontWeight: 700, fontSize: '0.85rem', marginBottom: '6px', color: '#1e293b' }}>+ Add Preference Category</div>
                  <input
                    type="text"
                    placeholder="Category Name (e.g. Personalized Advertising)"
                    value={newCatLabel}
                    onChange={e => setNewCatLabel(e.target.value)}
                    style={{ width: '100%', padding: '6px 8px', fontSize: '0.82rem', borderRadius: 6, border: '1px solid #cbd5e1', marginBottom: 6 }}
                  />
                  <input
                    type="text"
                    placeholder="Category Description"
                    value={newCatDesc}
                    onChange={e => setNewCatDesc(e.target.value)}
                    style={{ width: '100%', padding: '6px 8px', fontSize: '0.82rem', borderRadius: 6, border: '1px solid #cbd5e1', marginBottom: 6 }}
                  />
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.8rem', cursor: 'pointer', color: '#334155' }}>
                      <input type="checkbox" checked={newCatDefault} onChange={e => setNewCatDefault(e.target.checked)} />
                      Default ON for new users
                    </label>
                    <button
                      onClick={handleAddCategory}
                      style={{ background: '#2563eb', color: '#fff', border: 'none', padding: '6px 14px', borderRadius: 6, fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer' }}
                    >
                      Add
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* TAB 5: Data We Collect */}
            {activeTab === 'data' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <div style={{ fontSize: '0.8rem', color: '#64748b' }}>
                  Labels shown under "Data We Collect" in the banner.
                </div>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {(config.dataCollected || []).map((label, idx) => (
                    <span
                      key={idx}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        background: '#f1f5f9',
                        border: '1px solid #cbd5e1',
                        borderRadius: 16,
                        padding: '4px 10px',
                        fontSize: '0.8rem',
                        color: '#0f172a',
                        fontWeight: 600
                      }}
                    >
                      {label}
                      <button
                        onClick={() => handleRemoveDataTag(idx)}
                        style={{ background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer', fontWeight: 700, padding: 0, fontSize: '0.9rem' }}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>

                <div style={{ display: 'flex', gap: '6px', marginTop: '10px' }}>
                  <input
                    type="text"
                    placeholder="Enter data field (e.g. Location, Device ID)"
                    value={newDataTag}
                    onChange={e => setNewDataTag(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') handleAddDataTag(); }}
                    style={{ flex: 1, padding: '6px 10px', fontSize: '0.82rem', borderRadius: 6, border: '1px solid #cbd5e1' }}
                  />
                  <button
                    onClick={handleAddDataTag}
                    style={{ background: '#2563eb', color: '#fff', border: 'none', padding: '6px 14px', borderRadius: 6, fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer' }}
                  >
                    Add Tag
                  </button>
                </div>
              </div>
            )}

            {/* Big Prominent Save Button */}
            <button
              onClick={handleSaveToDatabase}
              disabled={saving}
              className="dpdp-btn dpdp-btn-primary"
              style={{
                marginTop: '1rem',
                width: '100%',
                background: config.primaryColor || '#2563eb',
                color: '#fff',
                padding: '12px',
                borderRadius: 8,
                fontWeight: 700,
                fontSize: '0.95rem',
                cursor: saving ? 'not-allowed' : 'pointer',
                boxShadow: '0 4px 12px rgba(37,99,235,0.25)',
                border: 'none'
              }}
            >
              {saving ? 'Saving to Database...' : '💾 Save Configuration to Database'}
            </button>
          </div>

          {/* 2. Live Preview Panel */}
          <div className="dpdp-panel-preview" style={{ background: '#f8fafc', border: '1.5px solid #e2e8f0', borderRadius: 12, padding: '16px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h3 style={{ margin: 0, fontSize: '1.05rem', color: '#0f172a' }}>Live Preview</h3>
              <span style={{ fontSize: '0.78rem', color: '#64748b' }}>Live Interactive Preview</span>
            </div>

            <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
              <DpdpConsentPreview
                config={config}
                tenantName={tenantName}
                onAccept={sendConsent}
                onDecline={sendConsent}
                onPreferences={() => setPrefsOpen(true)}
              />
            </div>
          </div>

          {/* 3. Embed & SDK Code Panel */}
          <div className="dpdp-panel-embed" style={{ background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 12, padding: '16px', display: 'flex', flexDirection: 'column' }}>
            <h4 style={{ margin: '0 0 8px 0', fontSize: '1rem', color: '#0f172a' }}>CDN Embed Code</h4>
            <span style={{ fontSize: '0.78rem', color: '#64748b', marginBottom: '10px' }}>
              Banner ID: <b>{activeBanner?.banner_id || 'banner_default'}</b>
            </span>

            <div className="dpdp-code-platforms" role="tablist" aria-label="Integration platform" style={{ display: 'flex', gap: '4px', marginBottom: '8px' }}>
              {[
                { id: 'web', label: 'Web Embed' },
                { id: 'ios', label: 'iOS SDK' },
                { id: 'android', label: 'Android' },
              ].map(platform => (
                <button
                  key={platform.id}
                  type="button"
                  role="tab"
                  aria-selected={codePlatform === platform.id}
                  className={codePlatform === platform.id ? 'is-active' : ''}
                  onClick={() => { setCodePlatform(platform.id); setCopied(false); }}
                  style={{
                    padding: '5px 10px',
                    borderRadius: 6,
                    border: '1px solid #cbd5e1',
                    background: codePlatform === platform.id ? '#2563eb' : '#f8fafc',
                    color: codePlatform === platform.id ? '#fff' : '#475569',
                    fontWeight: 600,
                    fontSize: '0.78rem',
                    cursor: 'pointer'
                  }}
                >
                  {platform.label}
                </button>
              ))}
            </div>

            <textarea
              readOnly
              value={integrationCode}
              className="dpdp-embed-textarea"
              style={{ width: '100%', height: '280px', fontSize: '0.78rem', fontFamily: 'monospace', padding: '10px', borderRadius: 8, border: '1.5px solid #cbd5e1', background: '#f8fafc', resize: 'vertical' }}
              aria-label={`${codePlatform} integration code`}
            />

            <button
              onClick={copyIntegrationCode}
              className="dpdp-copy-btn"
              style={{
                marginTop: '10px',
                padding: '10px',
                borderRadius: 8,
                border: 'none',
                background: copied ? '#10b981' : '#2563eb',
                color: '#fff',
                fontWeight: 700,
                cursor: 'pointer'
              }}
            >
              <FontAwesomeIcon icon={faCopy} /> {copied ? 'Copied to Clipboard!' : `Copy ${codePlatform === 'web' ? 'CDN' : codePlatform.toUpperCase() + ' SDK'} Code`}
            </button>
          </div>
        </div>
      )}

      {/* Quick Preview Modal for DPDP Consent Banner */}
      {previewModalBanner && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(15,23,42,0.6)',
          backdropFilter: 'blur(3px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 99999,
          padding: 16
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: 14,
            maxWidth: 720,
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto',
            padding: '24px',
            boxShadow: '0 20px 40px rgba(0,0,0,0.25)',
            border: '1.5px solid #e2e8f0'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, borderBottom: '1px solid #e2e8f0', paddingBottom: 12 }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '1.15rem', color: '#0f172a' }}>
                  Preview: {previewModalBanner.name}
                </h3>
                <span style={{ fontSize: '0.78rem', color: '#64748b' }}>
                  Banner ID: <code>{previewModalBanner.banner_id}</code> {previewModalBanner.is_default ? '★ (Default Banner)' : ''}
                </span>
              </div>
              <button
                type="button"
                onClick={() => setPreviewModalBanner(null)}
                style={{ background: 'none', border: 'none', fontSize: '1.1rem', cursor: 'pointer', color: '#64748b' }}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <DpdpConsentPreview
                config={normalizeConfig(previewModalBanner.config)}
                tenantName={tenantName}
                onAccept={() => { }}
                onDecline={() => { }}
                onPreferences={() => { }}
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 18, borderTop: '1px solid #e2e8f0', paddingTop: 14 }}>
              <button
                type="button"
                onClick={() => copyEmbedCodeForBanner(previewModalBanner)}
                style={{
                  padding: '8px 14px',
                  borderRadius: 7,
                  border: '1px solid #cbd5e1',
                  background: '#f8fafc',
                  color: '#334155',
                  fontWeight: 600,
                  fontSize: '0.82rem',
                  cursor: 'pointer'
                }}
              >
                <FontAwesomeIcon icon={faCode} /> Copy Embed Code
              </button>
              <button
                type="button"
                onClick={() => {
                  handleEditBannerFromList(previewModalBanner);
                  setPreviewModalBanner(null);
                }}
                style={{
                  padding: '8px 16px',
                  borderRadius: 7,
                  border: 'none',
                  background: '#2563eb',
                  color: '#ffffff',
                  fontWeight: 700,
                  fontSize: '0.82rem',
                  cursor: 'pointer'
                }}
              >
                <FontAwesomeIcon icon={faEdit} /> Open in Customizer
              </button>
              <button
                type="button"
                onClick={() => setPreviewModalBanner(null)}
                style={{
                  padding: '8px 14px',
                  borderRadius: 7,
                  border: '1px solid #cbd5e1',
                  background: '#fff',
                  color: '#64748b',
                  fontSize: '0.82rem',
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Create New Banner */}
      {showNewModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: '24px', width: '100%', maxWidth: '440px', boxShadow: '0 20px 40px rgba(0,0,0,0.2)' }}>
            <h3 style={{ margin: '0 0 12px 0', fontSize: '1.15rem', color: '#0f172a' }}>Create New Consent Banner</h3>
            <p style={{ fontSize: '0.85rem', color: '#64748b', margin: '0 0 16px 0' }}>
              Create an additional consent banner for this tenant with unique branding, preferences, and embed code.
            </p>

            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#334155', marginBottom: 4 }}>Banner Name:</label>
              <input
                type="text"
                placeholder="e.g. Mobile Web Gate, Checkout Notice"
                value={newBannerName}
                onChange={e => setNewBannerName(e.target.value)}
                style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: '1.5px solid #cbd5e1', fontSize: '0.9rem' }}
              />
            </div>

            <div style={{ marginBottom: '14px' }}>
              <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#334155', marginBottom: 4 }}>Description (Optional):</label>
              <input
                type="text"
                placeholder="e.g. Banner shown on customer checkout"
                value={newBannerDesc}
                onChange={e => setNewBannerDesc(e.target.value)}
                style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: '1.5px solid #cbd5e1', fontSize: '0.9rem' }}
              />
            </div>

            <div style={{ marginBottom: '18px' }}>
              <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#334155', marginBottom: 6 }}>Starting Template:</label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.85rem', color: '#334155', marginBottom: 6, cursor: 'pointer' }}>
                <input type="radio" name="tmpl" value="duplicate" checked={newBannerTemplate === 'duplicate'} onChange={() => setNewBannerTemplate('duplicate')} />
                Duplicate settings from current banner ({activeBanner?.name || 'Default'})
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.85rem', color: '#334155', cursor: 'pointer' }}>
                <input type="radio" name="tmpl" value="default" checked={newBannerTemplate === 'default'} onChange={() => setNewBannerTemplate('default')} />
                Start with clean OneDPDP statutory template
              </label>
            </div>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => setShowNewModal(false)}
                style={{ background: '#f1f5f9', border: '1px solid #cbd5e1', color: '#475569', padding: '8px 16px', borderRadius: 6, fontWeight: 600, cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCreateNewBanner}
                disabled={creatingBanner}
                style={{ background: '#059669', border: 'none', color: '#fff', padding: '8px 18px', borderRadius: 6, fontWeight: 700, cursor: 'pointer' }}
              >
                {creatingBanner ? 'Creating...' : 'Create Banner'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DPDP Preferences Modal (Preview Mode) */}
      {prefsOpen && (
        <DpdpConsentInterface
          initial={{}}
          onConfirm={async (prefs) => {
            await sendConsent('custom', prefs);
            setPrefsOpen(false);
          }}
          onClose={() => setPrefsOpen(false)}
        />
      )}
    </div>
  );
}

export default DpdpConsentPage;